import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { collection, addDoc, doc, getDoc, query, where, getDocs, deleteDoc, updateDoc, serverTimestamp } from '../../lib/auditedFirestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { logAuditAction } from '../../lib/auditLogger';

// Components
import BunnyVideoUploader from '../../components/BunnyVideoUploader';
import AdvancedWordProcessor from '../../components/editors/AdvancedWordProcessor';
import CustomSpreadsheet from '../../components/editors/CustomSpreadsheet';
import Spinner from '../../components/Spinner';
import ScenarioQuestionEditor from './ScenarioQuestionEditor';
import IQQuestionImport from './IQQuestionImport';
import { isScenarioRecord, sectionFor, managedQuestions } from '../practice/practiceSections';
import { deleteScenario } from '../practice/scenarioStorage';
import { buildQuestionEntry, validationError, plainText, removePairItem } from '../practice/practiceModel';
import { QuestionContent, AnswerInput, Explanation } from '../practice/PracticeQuestion';
import '../practice/practice.css';
import { validQuestionNumber } from '../practice/voiceCommands';
import { prepareQuestionForSave, resolveQuestionContentBatch } from '../practice/questionContentStorage';

const InstilIQManager = ({ paper, onBack, user, setModal }) => {
    const isProfessional = paper && paper.level === 'professional';

    const defaultQuestion = {
        questionNumber: '',
        difficulty: 'standard',
        tags: [],
        type: isProfessional ? 'descriptive' : 'mcq',
        text: '',
        images: [],
        options: ['', '', '', ''],
        correctAnswer: isProfessional ? '' : [],
        marks: isProfessional ? 10 : 1,
        explanation: '',
        explanationFormat: 'text',
        isProcessing: false,
        suggestedAnswerFormat: 'text',
        suggestedAnswerContent: '',
        suggestedAnswerExcel: null,
        suggestedAnswerView: 'main',
        markingSchemeFormat: 'text',
        markingSchemeContent: '',
        prompts: [], 
        rows: [], 
        columns: [], 
    };

    // --- State Management ---
    const [search, setSearch] = useState('');
    const [sectionFilter, setSectionFilter] = useState('');
    const [sortOrder, setSortOrder] = useState('newest');
    const [scenarioDraft, setScenarioDraft] = useState(null);
    const [scenarioVersion, setScenarioVersion] = useState(0);
    const [scenarioBusy, setScenarioBusy] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [uploadVersion,setUploadVersion] = useState(0);
    const [preview, setPreview] = useState(false);
    const [previewAnswer, setPreviewAnswer] = useState(undefined);
    const saveLock = useRef(false);
    const listRequest = useRef(0);
    const [activeTab, setActiveTab] = useState('add'); 
    const [questionsList, setQuestionsList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [listError, setListError] = useState('');
    const [saveStatus, setSaveStatus] = useState('');
    
    // Edit Tracking
    const [editingId, setEditingId] = useState(null);

    const [topic, setTopic] = useState('');
    const [isManualTopic, setIsManualTopic] = useState(false);
    const [configuredLibraryId, setConfiguredLibraryId] = useState('');
    const [videoLibraryId, setVideoLibraryId] = useState('');
    const bunnyLibraryId = videoLibraryId || configuredLibraryId;
    const [videoPending, setVideoPending] = useState(false);
    const videoPanelRef = useRef(null);
    const focusVideoRef = useRef(false);
    const [fetchingConfig, setFetchingConfig] = useState(true);
    const [bunnyVideoId, setBunnyVideoId] = useState('');
    const [q, setQ] = useState(defaultQuestion);
    const [loading, setLoading] = useState(false);

    // Refs for Drag and Drop Image Reordering
    const dragItem = useRef();
    const dragOverItem = useRef();

    const availableTopics = useMemo(() => {
        if (!paper) return [];
        if (paper.syllabus && Array.isArray(paper.syllabus)) {
            return [...new Set(paper.syllabus.map(s => s.topic))].filter(Boolean);
        }
        if (paper.sections && Array.isArray(paper.sections)) {
            return [...new Set(paper.sections.map(s => s.name))].filter(Boolean);
        }
        return [];
    }, [paper]);

    useEffect(() => {
        const fetchBunnyConfig = async () => {
            try {
                const configRef = doc(db, 'settings', 'bunnyConfig'); 
                const docSnap = await getDoc(configRef);
                if (docSnap.exists() && docSnap.data().libraryId) {
                    setConfiguredLibraryId(String(docSnap.data().libraryId));
                }
            } catch (error) {
                console.error("Error fetching Bunny config:", error);
            } finally {
                setFetchingConfig(false);
            }
        };
        fetchBunnyConfig();
    }, []);

    useEffect(() => () => { listRequest.current += 1; }, [paper?.id]);

    const fetchQuestions = useCallback(async () => {
        if (!paper?.id) return;
        const request = ++listRequest.current;
        setLoadingList(true);
        setListError('');
        setQuestionsList([]);
        try {
            const qRef = query(collection(db, 'practiceQuestions'), where('paperId', '==', paper.id));
            const snapshot = await getDocs(qRef);
            const fetched = await resolveQuestionContentBatch(snapshot.docs.map(item => ({ ...item.data(), id: item.id })));
            
            fetched.sort((a, b) => {
                const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return timeB - timeA;
            });
            if (request === listRequest.current) setQuestionsList(fetched);
        } catch (error) {
            console.error("Error fetching questions:", error);
            if (request === listRequest.current) setListError(`Failed to load questions: ${error.message}`);
        } finally {
            if (request === listRequest.current) setLoadingList(false);
        }
    }, [paper?.id]);

    useEffect(() => {
        if (activeTab === 'list') {
            fetchQuestions();
        }
    }, [activeTab, fetchQuestions]);

    const handleQuestionChange = (field, value) => {
        setQ(prev => ({ ...prev, [field]: value }));
    };

    const changeQuestionType = (type) => {
        if (type === q.type) return;
        let newQuestion = { ...defaultQuestion, type };
        if (type === 'mcq') newQuestion = { ...newQuestion, marks: 1, correctAnswer: [] };
        else if (type === 'descriptive') newQuestion = { ...newQuestion, marks: 10 };
        else if (type === 'dropdown') newQuestion = { ...newQuestion, correctAnswer: 0 };
        else if (type === 'mcq-grid') newQuestion = { ...newQuestion, rows: ['Row 1'], columns: ['Column 1'], correctAnswer: {} };
        else if (type === 'short-answer') newQuestion = { ...newQuestion, correctAnswer: '', marks: 2 };
        else if (type === 'matching') newQuestion = { ...newQuestion, prompts: ['Prompt 1', 'Prompt 2'], options: ['Option A', 'Option B'], correctAnswer: {}, marks: 4 };
        setQ({ ...newQuestion, questionNumber:q.questionNumber ?? '', text:q.text, images:q.images, difficulty:q.difficulty || 'standard', tags:q.tags || [], section:q.section, explanation:q.explanation, explanationFormat:q.explanationFormat });
    };

    const handleOptionChange = (oIndex, value) => {
        const newOptions = [...q.options];
        newOptions[oIndex] = value;
        setQ(prev => ({ ...prev, options: newOptions }));
    };

    const handleCorrectAnswerChange = (oIndex) => {
        if (q.type === 'mcq') {
            const currentCorrect = Array.isArray(q.correctAnswer) ? q.correctAnswer.map(Number) : q.correctAnswer == null ? [] : [Number(q.correctAnswer)];
            if (currentCorrect.includes(oIndex)) {
                setQ(prev => ({ ...prev, correctAnswer: currentCorrect.filter(i => i !== oIndex) }));
            } else {
                setQ(prev => ({ ...prev, correctAnswer: [...currentCorrect, oIndex] }));
            }
        } else if (q.type === 'dropdown') {
            setQ(prev => ({ ...prev, correctAnswer: oIndex }));
        }
    };

    const handleMatchingChange = (listType, itemIndex, value) => {
        const newList = [...q[listType]];
        newList[itemIndex] = value;
        setQ(prev => ({ ...prev, [listType]: newList }));
    };

    const addMatchingItem = (listType) => {
        setQ(prev => ({ ...prev, [listType]: [...prev[listType], ''] }));
    };

    const removeMatchingItem = (listType, itemIndex) => {
        setQ(prev => removePairItem(prev, listType, itemIndex));
    };

    const handleMatchingCorrectAnswerChange = (promptIndex, optionIndexStr) => {
        const optionIndex = optionIndexStr === "" ? undefined : parseInt(optionIndexStr, 10);
        const newCorrectAnswer = { ...q.correctAnswer };
        if (optionIndex === undefined) delete newCorrectAnswer[promptIndex];
        else newCorrectAnswer[promptIndex] = optionIndex;
        setQ(prev => ({ ...prev, correctAnswer: newCorrectAnswer }));
    };

    const handleImageUpload = (event, field, isMultiple) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;
        setQ(prev => ({ ...prev, isProcessing: true }));

        const uploadPromises = files.map(file => {
            const storagePath = field === 'images' ? 'exam-images' : 'descriptive-assets';
            const storageRef = ref(storage, `${storagePath}/${Date.now()}-${file.name}`);
            return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
        });

        Promise.all(uploadPromises).then(downloadURLs => {
            setQ(prev => {
                if (isMultiple) {
                    const existingImages = Array.isArray(prev[field]) ? prev[field] : (prev[field] ? [prev[field]] : []);
                    return { ...prev, [field]: [...existingImages, ...downloadURLs], isProcessing: false };
                } else {
                    return { ...prev, [field]: downloadURLs[0], isProcessing: false };
                }
            });
        }).catch(error => {
            console.error(`${field} upload failed:`, error);
            setModal({ show: true, message: `Image upload failed: ${error.message}` });
            setQ(prev => ({ ...prev, isProcessing: false }));
        });
    };

    const handleExplanationImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setQ(prev => ({ ...prev, isProcessing: true }));
        try {
            const storageRef = ref(storage, `explanation-images/${Date.now()}-${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            setQ(prev => ({ ...prev, explanation: downloadURL, isProcessing: false }));
        } catch (error) {
            console.error("Explanation image upload failed:", error);
            setModal({ show: true, message: `Explanation image upload failed: ${error.message}` });
            setQ(prev => ({ ...prev, isProcessing: false }));
        }
    };

    const handleImageReorder = (field = 'images') => {
        let currentImages = Array.isArray(q[field]) ? [...q[field]] : [q[field]].filter(Boolean);
        const draggedItemContent = currentImages.splice(dragItem.current, 1)[0];
        currentImages.splice(dragOverItem.current, 0, draggedItemContent);
        setQ(prev => ({ ...prev, [field]: currentImages }));
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const removeImage = (imgIndex, field = 'images') => {
        let currentImages = Array.isArray(q[field]) ? [...q[field]] : [q[field]].filter(Boolean);
        currentImages.splice(imgIndex, 1);
        setQ(prev => ({ ...prev, [field]: currentImages }));
    };

    useEffect(() => {
        if (activeTab === 'add' && focusVideoRef.current) {
            focusVideoRef.current = false;
            videoPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            videoPanelRef.current?.focus({ preventScroll: true });
        }
    }, [activeTab, editingId]);

    const handleEditQuestion = (item, focusVideo = false) => {
        if (isScenarioRecord(item) || item.scenarioId) {
            const shared = isScenarioRecord(item) ? item : questionsList.find(q => q.id === item.scenarioId);
            const parts = shared?.subquestionIds?.map(id => questionsList.find(q => q.id === id));
            if (!shared || parts?.length !== 5 || parts.some(q => !q || q.scenarioId !== shared.id)) { setModal({show:true,message:'This scenario has missing requirements. Reload the question list before editing.'}); return; }
            setScenarioDraft({...shared, parts}); setScenarioVersion(n => n + 1); setPreview(false); setActiveTab('scenario');
            window.scrollTo({top:0,behavior:'smooth'}); return;
        }
        focusVideoRef.current = focusVideo;
        setUploadVersion(n => n + 1);
        setVideoPending(false);
        setEditingId(item.id);
        setTopic(item.topic || '');
        setIsManualTopic(!availableTopics.includes(item.topic));
        setVideoLibraryId(item.bunnyVideoId ? String(item.bunnyLibraryId || '') : '');
        setBunnyVideoId(item.bunnyVideoId || '');
        setQ({ ...defaultQuestion, ...item, section:sectionFor(item), correctAnswer:item.type === 'mcq' ? (Array.isArray(item.correctAnswer) ? item.correctAnswer.map(Number) : item.correctAnswer == null ? [] : [Number(item.correctAnswer)]) : item.correctAnswer, isProcessing: false });
        setActiveTab('add');
        if (!focusVideo) window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setQ(defaultQuestion);
        setBunnyVideoId('');
        setVideoLibraryId('');
        if (isManualTopic) setTopic('');
        setActiveTab('list');
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();

        if (saveLock.current || q.isProcessing || videoPending) return;
        if (q.questionNumber !== '' && q.questionNumber != null && !validQuestionNumber(q.questionNumber)) { setModal({show:true,message:'Use a question number from 1 to 9999, or leave it blank for automatic library numbering.'}); return; }
        const error = validationError({ ...q, bunnyLibraryId, bunnyVideoId }, topic);
        if (error) { setModal({ show:true, message:error }); return; }
        saveLock.current = true;
        setLoading(true);
        setSaveStatus('Preparing question…');
        try {
            if (validQuestionNumber(q.questionNumber)) {
                const snapshot = await getDocs(query(collection(db,'practiceQuestions'),where('paperId','==',paper.id)));
                if (snapshot.docs.some(item=>item.id!==editingId && validQuestionNumber(item.data().questionNumber)===validQuestionNumber(q.questionNumber))) {
                    setModal({show:true,message:'That question number is already assigned in this subject. Choose another number.'}); return;
                }
            }
            const fullEntry = { ...buildQuestionEntry({...q, section:sectionFor(q)}, { topic, paper, bunnyLibraryId, bunnyVideoId }), questionNumber:validQuestionNumber(q.questionNumber) || '', updatedAt:serverTimestamp() };
            if (!editingId) fullEntry.createdAt = serverTimestamp();
            const savedEntry = await prepareQuestionForSave(fullEntry, { onStatus: setSaveStatus });
            setSaveStatus('Saving question…');

            if (editingId) {
                await updateDoc(doc(db, 'practiceQuestions', editingId), savedEntry);
                logAuditAction(user, 'UPDATE_INSTIL_IQ_QUESTION', `Updated Instil IQ question for topic: ${topic}`, fullEntry);
                setModal({ show: true, message: 'Question updated successfully!' });
            } else {
                await addDoc(collection(db, 'practiceQuestions'), savedEntry);
                logAuditAction(user, 'CREATE_INSTIL_IQ_QUESTION', `Added Instil IQ question for topic: ${topic}`, fullEntry);
                setModal({ show: true, message: 'Practice question added successfully!' });
            }
            
            setEditingId(null);
            setUploadVersion(n=>n+1);
            setQ(defaultQuestion);
            setBunnyVideoId('');
            setVideoLibraryId('');
            if (isManualTopic) setTopic('');
            setActiveTab('list');
            
        } catch (error) {
            console.error("Error saving Instil IQ question:", error);
            setModal({ show: true, message: `Failed to save question: ${error.message} Your draft is still in the editor; please try again.` });
        } finally {
            saveLock.current = false;
            setLoading(false);
            setSaveStatus('');
        }
    };

    const handleDeleteQuestion = async (id, questionTopic) => {
        const shared = questionsList.find(q => q.id === id && isScenarioRecord(q));
        if (shared) {
            if (!window.confirm(`Delete the scenario “${shared.title}” and all five requirements? This cannot be undone.`)) return;
            try { const removed = await deleteScenario(shared); setQuestionsList(items => items.filter(q => !removed.includes(q.id))); logAuditAction(user,'DELETE_INSTIL_IQ_SCENARIO',`Deleted scenario ${shared.title}`); setModal({show:true,message:'Scenario and five requirements deleted.'}); }
            catch (error) { setModal({show:true,message:`Could not delete this scenario: ${error.message}`}); }
            return;
        }
        if (!window.confirm(`Are you sure you want to delete this question for "${questionTopic}"? This action cannot be undone.`)) return;

        try {
            await deleteDoc(doc(db, 'practiceQuestions', id));
            setQuestionsList(prev => prev.filter(item => item.id !== id));
            logAuditAction(user, 'DELETE_INSTIL_IQ_QUESTION', `Deleted Instil IQ question in paper: ${paper.name}`);
            setModal({ show: true, message: 'Question deleted successfully.' });
        } catch (error) {
            console.error("Error deleting question:", error);
            setModal({ show: true, message: `Failed to delete question: ${error.message}` });
        }
    };

    const filteredQuestions = managedQuestions(questionsList, {search, section:sectionFilter, sort:sortOrder, text:plainText});
    return (
        <div className="iq-admin bg-white p-6 rounded-lg shadow-md border border-gray-200 animate-fade-in max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6 border-b pb-3">
                <div>
                    <h2 className="text-xl font-bold text-purple-700">Instil IQ · Question studio</h2>
                    <p className="text-sm text-gray-500">Selected Paper: <span className="font-semibold text-gray-800">{paper?.name}</span></p>
                </div>
                <button onClick={onBack} disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors">
                    ← Back to Papers
                </button>
            </div>

            <div className="flex flex-wrap gap-4 mb-6 border-b pb-2">
                <button 
                    disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy}
                    onClick={() => { setActiveTab('add'); setPreview(false); }}
                    className={`font-semibold pb-2 border-b-2 transition-colors ${activeTab === 'add' ? 'text-purple-600 border-purple-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                >
                    {editingId ? '✏️ Edit Question' : 'Add New Question'}
                </button>
                <button 
                    disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy}
                    onClick={() => { setActiveTab('list'); setEditingId(null); setQ(defaultQuestion); setBunnyVideoId(''); setVideoLibraryId(''); setUploadVersion(n => n + 1); }}
                    className={`font-semibold pb-2 border-b-2 transition-colors ${activeTab === 'list' ? 'text-purple-600 border-purple-600' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
                >
                    Manage Questions
                </button>
                <button disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy} onClick={() => {setScenarioDraft(null);setScenarioVersion(n => n + 1);setPreview(false);setActiveTab('scenario');}} className={`font-semibold pb-2 border-b-2 ${activeTab === 'scenario' ? 'text-purple-600 border-purple-600' : 'text-gray-500 border-transparent'}`}>Section B · Scenario OTQ</button>
                <button type="button" disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy} onClick={()=>{setPreview(false);setActiveTab('import');}} className={`font-semibold pb-2 border-b-2 ${activeTab==='import'?'text-purple-600 border-purple-600':'text-gray-500 border-transparent'}`}>Import from another paper</button>
            </div>

            {activeTab==='import' && <IQQuestionImport paper={paper} onBusyChange={setImportBusy}/>}
            <div className="iq-studio-banner"><div><span className="iq-eyebrow">GREAT PRACTICE STARTS WITH GREAT QUESTIONS</span><h3>Teach the thinking behind the answer.</h3><p>Publish your question and answer now. Add a recorded walkthrough whenever it is ready.</p></div><span>CREATE → EXPLAIN → INSPIRE</span></div>
            {activeTab === 'scenario' && <ScenarioQuestionEditor key={scenarioVersion} paper={paper} initial={scenarioDraft} topics={availableTopics} configuredLibraryId={configuredLibraryId} onBusyChange={setScenarioBusy} onCancel={() => setActiveTab('list')} onSaved={id => {logAuditAction(user,scenarioDraft ? 'UPDATE_INSTIL_IQ_SCENARIO' : 'CREATE_INSTIL_IQ_SCENARIO',`Saved Section B scenario ${id}`);setModal({show:true,message:'Shared scenario and all five requirements saved successfully!'});setActiveTab('list');}}/>}
            {preview && <section className="iq-admin-preview" aria-label="Student preview"><div className="iq-section-heading"><h3>Student preview</h3><button type="button" className="iq-secondary" onClick={()=>setPreview(false)}>Close preview</button></div><QuestionContent question={q}/><AnswerInput question={q} answer={previewAnswer} onChange={setPreviewAnswer}/><Explanation question={{...q,bunnyLibraryId,bunnyVideoId}} outcome="review" paper={paper}/></section>}
            {activeTab === 'add' && (
                <form onSubmit={handleSaveQuestion} className="space-y-6" aria-busy={loading}>
                    <p className="text-sm text-gray-600">Large questions, pasted images and worked answers are saved automatically. Keep this page open until saving finishes.</p>
                    {saveStatus && <p role="status" className="rounded-lg bg-purple-50 p-3 text-purple-800">{saveStatus}</p>}
                    <fieldset disabled={loading} inert={loading ? true : undefined} className="space-y-6 min-w-0">
                    <div className="iq-authoring-tools"><label>Exam section<select aria-label="Exam section" value={sectionFor(q)} onChange={e=>handleQuestionChange('section',e.target.value)}><option value="A">Section A</option><option value="B">Section B</option><option value="C">Section C</option></select></label><label>Question number (optional)<input aria-label="Question number" type="number" min="1" max="9999" step="1" value={q.questionNumber ?? ''} onChange={e=>handleQuestionChange('questionNumber',e.target.value)} placeholder="Permanent question number"/><small>Unique within this subject. Blank = automatic library number.</small></label><label>Difficulty<select value={q.difficulty || 'standard'} onChange={e=>handleQuestionChange('difficulty',e.target.value)}><option value="foundation">Foundation</option><option value="standard">Standard</option><option value="challenge">Challenge</option></select></label><label>Search tags (comma-separated)<input value={(q.tags || []).join(',')} onChange={e=>handleQuestionChange('tags',e.target.value.split(',').slice(0,12))} placeholder="cash flow, ratios, revision"/></label><button type="button" className="iq-secondary" onClick={()=>{setPreviewAnswer(undefined);setPreview(true);}}>Preview student experience</button></div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Topic Name</label>
                            {availableTopics.length > 0 && !isManualTopic ? (
                                <select 
                                    value={topic}
                                    onChange={(e) => {
                                        if (e.target.value === 'custom_manual_entry') {
                                            setIsManualTopic(true);
                                            setTopic('');
                                        } else {
                                            setTopic(e.target.value);
                                        }
                                    }}
                                    className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white cursor-pointer"
                                    required
                                >
                                    <option value="" disabled>-- Select a syllabus topic --</option>
                                    {availableTopics.map((t, idx) => (
                                        <option key={idx} value={t}>{t}</option>
                                    ))}
                                    <option disabled>────────────────────</option>
                                    <option value="custom_manual_entry" className="font-bold text-purple-600">➕ Add Custom Topic...</option>
                                </select>
                            ) : (
                                <div className="relative">
                                    <input 
                                        type="text"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        placeholder="e.g., Advanced Performance Measures"
                                        className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none pr-20"
                                        required
                                    />
                                    {availableTopics.length > 0 && (
                                        <button 
                                            type="button"
                                            onClick={() => { setIsManualTopic(false); setTopic(''); }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-purple-600 font-semibold bg-gray-100 px-2 py-1 rounded transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            )}
                            {availableTopics.length === 0 && <p className="text-[10px] text-orange-500 mt-1">No topics found in paper syllabus. Manual entry mode active.</p>}
                        </div>

                        <section ref={videoPanelRef} tabIndex={-1} aria-label="Optional explanation video" className="md:col-span-2 rounded-lg border border-purple-100 bg-white p-4 focus:outline-none focus:ring-2 focus:ring-purple-500">
                            <h4 className="font-semibold text-gray-800">Explanation video <span className="text-sm font-normal text-gray-500">(optional)</span></h4>
                            <p className="text-sm text-gray-600 mt-1 mb-3">Students can practise and see your answer without a video. To add one later, open Manage Questions and choose Add video or Edit.</p>
                            {bunnyLibraryId ? <BunnyVideoUploader key={`${editingId || 'new'}-${uploadVersion}-${bunnyLibraryId}`}
                                libraryId={bunnyLibraryId}
                                paperId={paper.id}
                                videoTitle={`${paper?.name} - ${topic || 'Instil IQ Q'}`}
                                initialVideoId={bunnyVideoId}
                                onUploadSuccess={(uploadedVideoId, uploadedLibraryId) => { setVideoLibraryId(uploadedLibraryId || bunnyLibraryId); setBunnyVideoId(uploadedVideoId); }}
                                onRemove={() => setBunnyVideoId('')}
                                onPendingChange={setVideoPending}
                            /> : <p className="text-sm bg-purple-50 rounded-lg p-3 text-purple-800">{fetchingConfig ? 'Checking video upload availability. You can save your question while this loads.' : 'Video uploading is not configured yet. You can still publish the question and answer now.'}</p>}
                            {videoPending && <p role="status" className="mt-3 text-sm text-amber-800">Finish uploading or clear the selected video before saving this question.</p>}
                        </section>
                    </div>

                    <div className="pt-4 border-t">
                        <h4 className="font-bold text-gray-700 mb-4">Question Configuration</h4>
                        
                        <div className="flex flex-wrap gap-3 mb-6">
                            {!isProfessional && (
                                <>
                                    <button type="button" onClick={() => changeQuestionType('mcq')} className={`font-bold py-2 px-4 rounded ${q.type === 'mcq' ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'}`}>MCQ</button>
                                    <button type="button" onClick={() => changeQuestionType('dropdown')} className={`font-bold py-2 px-4 rounded ${q.type === 'dropdown' ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'}`}>Drop Down</button>
                                    <button type="button" onClick={() => changeQuestionType('short-answer')} className={`font-bold py-2 px-4 rounded ${q.type === 'short-answer' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'}`}>Short Answer</button>
                                    <button type="button" onClick={() => changeQuestionType('matching')} className={`font-bold py-2 px-4 rounded ${q.type === 'matching' ? 'bg-pink-600 text-white' : 'bg-pink-100 text-pink-800 hover:bg-pink-200'}`}>Matching</button>
                                    <button type="button" onClick={() => changeQuestionType('mcq-grid')} className={`font-bold py-2 px-4 rounded ${q.type === 'mcq-grid' ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'}`}>MC Grid</button>
                                </>
                            )}
                            <button type="button" onClick={() => changeQuestionType('descriptive')} className={`font-bold py-2 px-4 rounded ${q.type === 'descriptive' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'}`}>Descriptive</button>
                        </div>

                        <div className="p-5 border rounded-lg bg-gray-50/50 space-y-4 shadow-sm">
                            <div className="flex justify-between items-center mb-2 border-b pb-2">
                                <h4 className="font-semibold text-lg text-gray-800">{q.type.replace('-', ' ').toUpperCase()} Format</h4>
                                <div>
                                    <label className="text-gray-600 text-[10px] font-bold mr-2 uppercase">Marks Allocation:</label>
                                    <input
                                        type="number"
                                        value={q.marks || ''}
                                        onChange={(e) => handleQuestionChange('marks', e.target.value === '' ? '' : Number(e.target.value))}
                                        className="w-20 p-1 border rounded text-center font-bold"
                                        required min="1"
                                    />
                                </div>
                            </div>

                            {/* Text & Image Uploads */}
                            <div>
                                <label className="block text-gray-700 font-bold mb-2">Question Text</label>
                                <div className="bg-white border rounded min-h-[150px] overflow-hidden mb-4">
                                    <AdvancedWordProcessor value={q.text || ''} onChange={(val) => handleQuestionChange('text', val)} />
                                </div>
                                
                                <div className="mt-4">
                                    <label className="block text-gray-700 font-bold mb-2">Question Images (Optional)</label>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-2">
                                        {Array.isArray(q.images) && q.images.map((imgSrc, imgIndex) => (
                                            <div key={imgIndex} className="relative group border-2 border-dashed rounded-lg p-1 cursor-grab" draggable onDragStart={(e) => { e.stopPropagation(); dragItem.current = imgIndex; }} onDragEnter={(e) => { e.stopPropagation(); dragOverItem.current = imgIndex; }} onDragEnd={(e) => { e.stopPropagation(); handleImageReorder('images'); }} onDragOver={(e) => e.preventDefault()}>
                                                <img src={imgSrc} alt={`Question part ${imgIndex + 1}`} className="w-full h-24 object-contain rounded"/>
                                                <button type="button" onClick={() => removeImage(imgIndex, 'images')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
                                            </div>
                                        ))}
                                    </div>
                                    <label htmlFor="q-img-upload" className="mt-2 cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block">
                                        {q.isProcessing ? 'Processing...' : 'Upload Image(s)'}
                                    </label>
                                    <input id="q-img-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'images', true)} accept="image/*" multiple />
                                </div>
                            </div>

                            {q.type === 'mcq' && (
                                <>
                                    <h5 className="font-semibold text-gray-700 pt-2">Options (Select all correct answers)</h5>
                                    {q.options.map((opt, oIndex) => (
                                        <div key={oIndex} className="flex items-center gap-3">
                                            <input type="checkbox" checked={(Array.isArray(q.correctAnswer) ? q.correctAnswer : [q.correctAnswer]).map(Number).includes(oIndex)} onChange={() => handleCorrectAnswerChange(oIndex)} className="h-4 w-4 rounded"/>
                                            <input type="text" value={opt} onChange={(e) => handleOptionChange(oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} required />
                                        </div>
                                    ))}
                                </>
                            )}

                            {q.type === 'dropdown' && (
                                <>
                                    <h5 className="font-semibold text-gray-700 pt-2">Options (Select the correct answer)</h5>
                                    {q.options.map((opt, oIndex) => (
                                        <div key={oIndex} className="flex items-center gap-3">
                                            <input type="radio" name="correctAnswer" checked={q.correctAnswer === oIndex} onChange={() => handleQuestionChange('correctAnswer', oIndex)} />
                                            <input type="text" value={opt} onChange={(e) => handleOptionChange(oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} required />
                                        </div>
                                    ))}
                                </>
                            )}
                            
                            {q.type === 'short-answer' && (
                                <div>
                                    <h5 className="font-semibold text-gray-700 pt-2">Correct Answer</h5>
                                    <p className="text-xs text-gray-500 mb-1">Student's answer must be an exact match (case-insensitive).</p>
                                    <input type="text" value={q.correctAnswer || ''} onChange={(e) => handleQuestionChange('correctAnswer', e.target.value)} className="w-full p-2 border rounded" placeholder="Enter the exact correct answer" required />
                                </div>
                            )}
                            
                            {q.type === 'mcq-grid' && <div className="iq-grid-editor"><h5>Grid columns</h5>{q.columns.map((column,i)=><div key={i}><input aria-label={`Column ${i+1}`} value={column} onChange={e=>handleMatchingChange('columns',i,e.target.value)}/><button type="button" onClick={()=>removeMatchingItem('columns',i)}>Remove column</button></div>)}<button type="button" onClick={()=>addMatchingItem('columns')}>+ Add column</button><h5>Rows and correct answers</h5>{q.rows.map((row,i)=><div key={i}><input aria-label={`Row ${i+1}`} value={row} onChange={e=>handleMatchingChange('rows',i,e.target.value)}/><select aria-label={`Correct column for row ${i+1}`} value={q.correctAnswer[i] ?? ''} onChange={e=>handleMatchingCorrectAnswerChange(i,e.target.value)}><option value="">Correct column…</option>{q.columns.map((c,j)=><option key={j} value={j}>{c || `Column ${j+1}`}</option>)}</select><button type="button" onClick={()=>removeMatchingItem('rows',i)}>Remove row</button></div>)}<button type="button" onClick={()=>addMatchingItem('rows')}>+ Add row</button></div>}
                            {q.type === 'matching' && (
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <h5 className="font-semibold text-gray-700">Prompts</h5>
                                        {q.prompts.map((prompt, pIndex) => (
                                            <div key={pIndex} className="flex items-center gap-2 mt-1">
                                                <input type="text" value={prompt} onChange={(e) => handleMatchingChange('prompts', pIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Prompt ${pIndex + 1}`} />
                                                <button type="button" onClick={() => removeMatchingItem('prompts', pIndex)} className="text-red-500 hover:text-red-700">&times;</button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={() => addMatchingItem('prompts')} className="text-sm text-purple-600 hover:underline mt-1">Add Prompt</button>
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-gray-700">Options</h5>
                                        {q.options.map((option, oIndex) => (
                                            <div key={oIndex} className="flex items-center gap-2 mt-1">
                                                <input type="text" value={option} onChange={(e) => handleMatchingChange('options', oIndex, e.target.value)} className="w-full p-2 border rounded" placeholder={`Option ${oIndex + 1}`} />
                                                <button type="button" onClick={() => removeMatchingItem('options', oIndex)} className="text-red-500 hover:text-red-700">&times;</button>
                                            </div>
                                        ))}
                                        <button type="button" onClick={() => addMatchingItem('options')} className="text-sm text-purple-600 hover:underline mt-1">Add Option</button>
                                    </div>
                                    <div className="col-span-2">
                                         <h5 className="font-semibold text-gray-700">Correct Pairs</h5>
                                         {q.prompts.map((prompt, pIndex) => (
                                             <div key={pIndex} className="flex items-center gap-4 mt-2 p-2 bg-gray-100 rounded">
                                                <span className="flex-1 font-medium">{prompt || `Prompt ${pIndex + 1}`}</span>
                                                <span>&#8594;</span>
                                                <select value={q.correctAnswer[pIndex] ?? ''} onChange={(e) => handleMatchingCorrectAnswerChange(pIndex, e.target.value)} className="flex-1 p-2 border rounded bg-white">
                                                    <option value="">Select a match...</option>
                                                    {q.options.map((option, oIndex) => (
                                                        <option key={oIndex} value={oIndex}>{option || `Option ${oIndex + 1}`}</option>
                                                    ))}
                                                </select>
                                             </div>
                                         ))}
                                    </div>
                                </div>
                            )}

                            {/* --- ANSWER EXPLANATION INTEGRATION --- */}
                            {['mcq', 'dropdown', 'mcq-grid', 'short-answer', 'matching'].includes(q.type) && (
                                <div className="mt-4 pt-4 border-t border-gray-300">
                                    <h5 className="font-semibold text-gray-700">Answer Explanation</h5>
                                    <div className="flex items-center gap-2 my-2">
                                        <span className="text-sm font-medium text-gray-600">Format:</span>
                                        <button type="button" onClick={() => handleQuestionChange('explanationFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.explanationFormat === 'text' || !q.explanationFormat ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                        <button type="button" onClick={() => handleQuestionChange('explanationFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.explanationFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                    </div>
                                    {q.explanationFormat === 'image' ? (
                                        <div>
                                            {q.explanation && (
                                                <div className="relative w-48 my-2">
                                                    <img src={q.explanation} alt="Explanation" className="border rounded-lg object-contain h-24 w-48"/>
                                                    <button type="button" onClick={() => handleQuestionChange('explanation', '')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">&times;</button>
                                                </div>
                                            )}
                                            <label htmlFor="exp-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block">{q.isProcessing ? "Processing..." : "Upload Image"}</label>
                                            <input id="exp-upload" type="file" className="hidden" onChange={handleExplanationImageUpload} accept="image/*" />
                                        </div>
                                    ) : (
                                        <textarea value={q.explanation || ''} onChange={(e) => handleQuestionChange('explanation', e.target.value)} className="w-full p-2 border rounded" placeholder="Provide a detailed explanation for the correct answer." rows="3"/>
                                    )}
                                </div>
                            )}

                            {q.type === 'descriptive' && (
                                <div className="space-y-4 pt-4 border-t">
                                    <div>
                                        <h5 className="font-semibold text-gray-700">Suggested Answer</h5>
                                        <p className="text-xs text-gray-500 mb-2">You can provide a Word/Image based answer and/or a Spreadsheet answer. Students can use both to review their own answers.</p>
                                        
                                        <div className="flex gap-2 mb-2 border-b">
                                            <button type="button" onClick={() => handleQuestionChange('suggestedAnswerView', 'main')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${!q.suggestedAnswerView || q.suggestedAnswerView === 'main' ? 'bg-white border-t border-l border-r border-gray-300 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                                Main Answer (Word/Image)
                                            </button>
                                            <button type="button" onClick={() => handleQuestionChange('suggestedAnswerView', 'excel')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${q.suggestedAnswerView === 'excel' ? 'bg-white border-t border-l border-r border-gray-300 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                                Spreadsheet Answer
                                            </button>
                                        </div>

                                        {(!q.suggestedAnswerView || q.suggestedAnswerView === 'main') && (
                                            <div className="bg-white p-2 rounded-b-lg border border-t-0 border-gray-300">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="text-sm font-medium text-gray-600">Format:</span>
                                                    <button type="button" onClick={() => handleQuestionChange('suggestedAnswerFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.suggestedAnswerFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                                    <button type="button" onClick={() => handleQuestionChange('suggestedAnswerFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.suggestedAnswerFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                                </div>

                                                {q.suggestedAnswerFormat === 'image' ? (
                                                    <div>
                                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-2 mb-2">
                                                            {(Array.isArray(q.suggestedAnswerContent) ? q.suggestedAnswerContent : (q.suggestedAnswerContent ? [q.suggestedAnswerContent] : [])).map((imgSrc, imgIndex) => (
                                                                <div key={imgIndex} className="relative group border-2 border-dashed rounded-lg p-1 cursor-grab" draggable onDragStart={(e) => { e.stopPropagation(); dragItem.current = imgIndex; }} onDragEnter={(e) => { e.stopPropagation(); dragOverItem.current = imgIndex; }} onDragEnd={(e) => { e.stopPropagation(); handleImageReorder('suggestedAnswerContent'); }} onDragOver={(e) => e.preventDefault()}>
                                                                    <img src={imgSrc} alt={`Suggested Answer ${imgIndex + 1}`} className="w-full h-24 object-contain rounded"/>
                                                                    <button type="button" onClick={() => removeImage(imgIndex, 'suggestedAnswerContent')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">&times;</button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <label htmlFor="sa-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 inline-block">
                                                            {q.isProcessing ? 'Processing...' : 'Upload Image(s)'}
                                                        </label>
                                                        <input id="sa-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'suggestedAnswerContent', true)} accept="image/*" multiple />
                                                    </div>
                                                ) : (
                                                    <div className="h-[400px] border rounded overflow-hidden">
                                                        <AdvancedWordProcessor value={q.suggestedAnswerContent || ''} onChange={(content) => handleQuestionChange('suggestedAnswerContent', content)} />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {q.suggestedAnswerView === 'excel' && (
                                            <div className="bg-white p-2 rounded-b-lg border border-t-0 border-gray-300 h-[500px]">
                                                <CustomSpreadsheet data={q.suggestedAnswerExcel} onChange={(data) => handleQuestionChange('suggestedAnswerExcel', data)} />
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div>
                                        <h5 className="font-semibold text-gray-700">Marking Scheme</h5>
                                        <div className="flex items-center gap-2 my-2">
                                            <span className="text-sm font-medium text-gray-600">Format:</span>
                                            <button type="button" onClick={() => handleQuestionChange('markingSchemeFormat', 'text')} className={`px-3 py-1 text-sm rounded-md ${q.markingSchemeFormat === 'text' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Text</button>
                                            <button type="button" onClick={() => handleQuestionChange('markingSchemeFormat', 'image')} className={`px-3 py-1 text-sm rounded-md ${q.markingSchemeFormat === 'image' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>Image</button>
                                        </div>
                                        {q.markingSchemeFormat === 'image' ? (
                                            <div>
                                                {q.markingSchemeContent && <img src={q.markingSchemeContent} alt="Marking Scheme" className="border rounded-lg h-24 w-auto my-2"/>}
                                                <label htmlFor="ms-upload" className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">Upload Image</label>
                                                <input id="ms-upload" type="file" className="hidden" onChange={(e) => handleImageUpload(e, 'markingSchemeContent', false)} accept="image/*" />
                                            </div>
                                        ) : (
                                            <div className="h-[300px] border rounded overflow-hidden">
                                                <AdvancedWordProcessor value={q.markingSchemeContent || ''} onChange={(content) => handleQuestionChange('markingSchemeContent', content)} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t gap-3">
                        {editingId && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold px-6 py-3 rounded-lg shadow transition-all text-lg"
                            >
                                Cancel Edit
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={loading || q.isProcessing || videoPending || scenarioBusy || importBusy}
                            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-lg shadow transition-all disabled:opacity-50 text-lg flex items-center justify-center min-w-[200px]"
                        >
                            {loading ? <Spinner /> : editingId ? 'Update Practice Entry' : 'Save Instil IQ Practice Entry'}
                        </button>
                    </div>
                    </fieldset>
                </form>
            )}

            {/* TAB CONTENT: MANAGE QUESTIONS */}
            {activeTab === 'list' && (
                <div className="animate-fade-in space-y-4"><div className="iq-search"><input aria-label="Search managed questions" placeholder="Find a question by text, topic, or tag…" value={search} onChange={e=>setSearch(e.target.value)}/><span>{filteredQuestions.length} entries</span></div><div className="iq-authoring-tools"><label>Filter by section<select value={sectionFilter} onChange={e=>setSectionFilter(e.target.value)}><option value="">All sections</option><option value="A">Section A</option><option value="B">Section B</option><option value="C">Section C</option></select></label><label>Sort questions<select value={sortOrder} onChange={e=>setSortOrder(e.target.value)}><option value="newest">Newest first</option><option value="section">Section A → B → C</option><option value="number">Question number</option></select></label></div><p className="iq-muted">A scenario appears once here with five requirements. Older questions without a section appear in A (objective) or C (written); choose Edit to change their section.</p>
                    {loadingList ? (
                        <div className="flex justify-center p-8"><Spinner /></div>
                    ) : listError ? (
                        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"><p>{listError}</p><button type="button" onClick={fetchQuestions} className="mt-3 font-semibold underline">Retry loading questions</button></div>
                    ) : filteredQuestions.length === 0 ? (
                        <div className="text-center p-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                            <p className="text-gray-500">No questions match this view. Clear your search or add a new question.</p>
                            <button 
                                onClick={() => { setActiveTab('add'); setPreview(false); }}
                                className="mt-4 text-purple-600 hover:underline font-medium"
                            >
                                Add your first question
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white rounded-lg overflow-hidden border border-gray-200">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Added</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredQuestions.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.questionNumber ? `Q${item.questionNumber} · ` : ''}{item.topic || 'N/A'}<p className="iq-table-question">{item.title || plainText(item.text) || 'Image question'}</p><span className="iq-muted">{item.bunnyVideoId && item.bunnyLibraryId ? '▷ Video attached' : 'Video can be added later'} · {item.difficulty || 'standard'}</span></td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase"><span className="iq-section-badge">Section {sectionFor(item)}</span><br/>{isScenarioRecord(item) ? 'Scenario · 5 requirements' : item.type?.replace('-', ' ')}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-bold">{item.marks}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {!(item.bunnyVideoId && item.bunnyLibraryId) && <button
                                                    type="button"
                                                    onClick={() => handleEditQuestion(item, true)}
                                                    className="text-purple-700 bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded mr-2"
                                                >Add video</button>}
                                                <button 
                                                    onClick={() => handleEditQuestion(item)}
                                                    className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded transition-colors mr-2"
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    onClick={() => handleDeleteQuestion(item.id, item.topic)}
                                                    className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InstilIQManager;